import { Outlet, useNavigate } from 'react-router-dom';
// Bell fica comentado junto com o botao de notificacoes na topbar (ver abaixo).
import { /* Bell, */ ChevronDown, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { useNoScrollNumberInput } from '@/hooks/useNoScrollNumberInput';
import { Sidebar } from '@/components/Sidebar';
import { GlobalSearch } from '@/components/GlobalSearch';
import { ContextoNavegacaoBadge } from '@/components/ContextoNavegacaoBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// AppShell:
// - Sidebar com tres estados (expandida/recolhida/hover) - ver components/Sidebar.tsx
// - Topbar 68px: ContextoNavegacaoBadge (cliente/rede + tooltip + switch
//   condicional) a esquerda, search + user menu a direita. O sino de
//   notificacoes esta comentado ate a feature existir de fato.
// - Margin-left do main acompanha SEMPRE o estado fixado da sidebar (collapsed),
//   nunca o hover-expand. Isso evita reflow do conteudo ao passar mouse.
// - useNoScrollNumberInput global

function AppShell() {
  useNoScrollNumberInput();

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeProfile } = useProfile();
  const { collapsed } = useSidebar();

  const initials =
    user?.nome
      ?.split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() ?? '?';

  // Margin-left segue o estado FIXADO da sidebar (nao o hover) pra evitar
  // que o conteudo se mova quando o usuario passa o mouse. A classe
  // .app-shell-main em index.css consome --current-sidebar-width via media
  // query lg+. Em mobile a sidebar e hidden e o margin fica em 0.
  const currentSidebarWidth = collapsed
    ? 'var(--sidebar-width-collapsed)'
    : 'var(--sidebar-width)';

  return (
    <div className="min-h-screen bg-bg">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-tooltip focus:bg-surface focus:text-primary focus:font-semibold focus:px-3 focus:py-1.5 focus:rounded-md focus:shadow-pop focus:border focus:border-primary"
      >
        Pular para conteúdo
      </a>

      <Sidebar />

      <div
        className={cn('min-h-screen flex flex-col app-shell-main')}
        style={{ ['--current-sidebar-width' as string]: currentSidebarWidth } as React.CSSProperties}
      >
        <header className="h-[68px] border-b border-border bg-surface/80 backdrop-blur-lg sticky top-0 z-30 px-6 lg:px-8 flex items-center justify-between">
          {/* Breadcrumb + cliente ativo */}
          <div className="flex items-center min-w-0">
            {/* Contexto de navegacao: cliente ou rede ativo + tooltip
                + botao switch condicional (super_admin / cliente_master /
                rede_master). Sem nome da pagina (ja tem em breadcrumb
                interno + sidebar). */}
            <ContextoNavegacaoBadge />
          </div>

          {/* Search + user */}
          <div className="flex items-center gap-3">
            {/* GlobalSearch so aparece no perfil frotista (decisao CEO
                2026-05-04). Posto e Admin nao tem busca global. */}
            {activeProfile === 'frotista' ? (
              <div className="hidden lg:block">
                <GlobalSearch />
              </div>
            ) : null}

            {/* Sino de notificacoes: DESATIVADO em 2026-08-13.
                Era placeholder de layout desde o cutover React. O botao nao
                tinha onClick e o ponto verde era estatico (sempre visivel,
                pra todo usuario, em todo perfil), entao indicava pendencia
                que nao existia e nao abria painel nenhum. Nao ha backend por
                tras: sem model no schema.prisma, sem modulo em apps/api/src,
                sem service em src/services.
                Reativar junto com a feature real (listar + marcar como lida),
                trocando o ponto estatico por contador vindo da API. */}
            {/*
            <button
              type="button"
              aria-label="Notificações"
              className="relative w-10 h-10 flex items-center justify-center rounded-lg border border-border hover:border-border-strong hover:bg-surface transition"
            >
              <Bell className="w-4 h-4 text-text-muted" />
              <span
                className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full ring-2 ring-surface"
                style={{ background: 'var(--color-success)' }}
              />
            </button>

            <div className="h-6 w-px bg-border" />
            */}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface border border-transparent hover:border-border transition"
                  aria-label="Menu do usuário"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--brand-goiaba-400), var(--brand-goiaba-600))',
                    }}
                  >
                    {initials}
                  </div>
                  <span className="text-sm font-medium text-text-strong hidden sm:inline">
                    {firstName(user?.nome)}
                  </span>
                  <ChevronDown className="w-4 h-4 text-text-subtle" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]">
                <DropdownMenuLabel>{user?.nome}</DropdownMenuLabel>
                <p className={cn('px-2 pb-2 text-xs text-text-muted truncate')}>{user?.email}</p>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/minha-conta')}>
                  Minha conta
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  danger
                  onSelect={() => {
                    logout();
                  }}
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main id="main-content" className="flex-1 min-w-0" role="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SidebarProvider>
        <AppShell />
      </SidebarProvider>
    </ThemeProvider>
  );
}

function firstName(nome: string | undefined): string {
  if (!nome) return '-';
  return nome.split(' ')[0];
}
